/**
 * Tagada Rx — which GLP-1 the weight loss storefront sells.
 *
 * One molecule per patient: the clinician prescribes semaglutide or
 * tirzepatide, never both. The merchant picks one, or lets the patient choose
 * in the quiz. Saved changes go live on the storefront right away.
 */

'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import {
  formatCents,
  useRxSetWeightLossProgram,
  useRxWeightLossProgram,
  type RxWeightLossProgram,
} from '@/lib/api/rx';

type Mode = RxWeightLossProgram['mode'];
const MODES: Mode[] = ['semaglutide', 'tirzepatide', 'patient_choice'];

export function WeightLossProgramCard({ storeId }: { storeId: string }) {
  const { t } = useTranslation('rx');
  const { toast } = useToast();
  const query = useRxWeightLossProgram(storeId);
  const save = useRxSetWeightLossProgram();
  const [mode, setMode] = React.useState<Mode>('semaglutide');

  React.useEffect(() => {
    if (query.data) setMode(query.data.mode);
  }, [query.data]);

  const data = query.data;
  if (query.isLoading) {
    return (
      <Card>
        <CardContent className="text-muted-foreground flex items-center gap-2 p-6 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('weightLossProgram.loading')}
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const option = (molecule: 'semaglutide' | 'tirzepatide') =>
    data.options.find((o) => o.molecule === molecule);
  const isAvailable = (m: Mode) =>
    m === 'patient_choice'
      ? !!option('semaglutide')?.available && !!option('tirzepatide')?.available
      : !!option(m)?.available;
  const priceLabel = (molecule: 'semaglutide' | 'tirzepatide') => {
    const o = option(molecule);
    if (!o?.available || o.amountMinor == null) return null;
    return `${formatCents(o.amountMinor, o.currency)}${o.interval === 'month' ? t('weightLossProgram.perMonth') : ''}`;
  };

  const handleSave = async () => {
    try {
      await save.mutateAsync({ storeId, mode });
      toast({
        title: t('weightLossProgram.toasts.savedTitle'),
        description: data.published
          ? t('weightLossProgram.toasts.savedLive')
          : t('weightLossProgram.toasts.savedDraft'),
      });
      await query.refetch();
    } catch (err) {
      toast({
        title: t('weightLossProgram.toasts.saveFailedTitle'),
        description: err instanceof Error ? err.message : t('weightLossProgram.toasts.unknownError'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('weightLossProgram.title')}</CardTitle>
        <CardDescription>{t('weightLossProgram.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <RadioGroup
          value={mode}
          onValueChange={(v) => setMode(v as Mode)}
          className="grid gap-3 lg:grid-cols-3"
        >
          {MODES.map((m) => {
            const available = isAvailable(m);
            const price = m === 'patient_choice' ? null : priceLabel(m as 'semaglutide' | 'tirzepatide');
            return (
              <Label
                key={m}
                htmlFor={`wl-${m}`}
                className={cn(
                  'flex cursor-pointer flex-col gap-1 rounded-lg border p-4 font-normal',
                  mode === m && 'border-primary ring-primary/20 ring-2',
                  !available && 'cursor-not-allowed opacity-50',
                )}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem id={`wl-${m}`} value={m} disabled={!available} />
                  <span className="font-medium">{t(`weightLossProgram.modes.${m}.title`)}</span>
                  {m === 'semaglutide' ? (
                    <Badge variant="secondary">{t('weightLossProgram.default')}</Badge>
                  ) : null}
                  {data.liveMode === m ? (
                    <Badge variant="outline">{t('weightLossProgram.live')}</Badge>
                  ) : null}
                </div>
                <span className="text-muted-foreground text-xs">
                  {t(`weightLossProgram.modes.${m}.body`)}
                </span>
                {price ? <span className="text-xs font-medium">{price}</span> : null}
                {!available ? (
                  <span className="text-muted-foreground text-xs">{t('weightLossProgram.unavailable')}</span>
                ) : null}
              </Label>
            );
          })}
        </RadioGroup>

        <ul className="text-muted-foreground list-disc space-y-1 pl-5 text-xs">
          <li>{t('weightLossProgram.why.onePerPatient')}</li>
          <li>{t('weightLossProgram.why.howToChoose')}</li>
          <li>{t('weightLossProgram.why.switching')}</li>
        </ul>

        <div className="flex items-center gap-3">
          <Button
            onClick={() => void handleSave()}
            disabled={save.isPending || mode === data.mode || !isAvailable(mode)}
          >
            {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t('weightLossProgram.save')}
          </Button>
          <span className="text-muted-foreground text-xs">
            {data.published ? t('weightLossProgram.appliesLive') : t('weightLossProgram.appliesOnPublish')}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
